import { WEBGL } from 'three/examples/jsm/WebGL.js';
import { Viewer } from './viewer.js';
import { SimpleDropzone } from 'simple-dropzone';
import queryString from 'query-string';

if (!(window.File && window.FileReader && window.FileList && window.Blob)) {
  console.error('The File APIs are not fully supported in this browser.');
} else if (!WEBGL.isWebGLAvailable()) {
  console.error('WebGL is not supported in this browser.');
}

class App {
  /**
   * @param  {Element} el
   * @param  {Location} location
   */
  constructor(el, location) {
    window.loadModel = this.loadModel.bind(this);
    window.sectionClick = this.sectionClick.bind(this);
    window.activateSelector = this.activateSelector;

    // document.getElementById('type_p_img').addEventListener(
    //   'mouseover',
    //   (e) => {
    //     e.target.nextElementSibling.style.display = 'inherit';
    //   },
    //   false
    // );
    // document.getElementById('type_p_img').parentElement.addEventListener(
    //   'mouseleave',
    //   (e) => {
    //     e.target.lastElementChild.style.display = null;
    //   },
    //   false
    // );

    const hash = location.hash ? queryString.parse(location.hash) : {};
    if (hash.model) {
      switch (hash.model) {
        case 'P11_Cage': {
          hash.model = 'assets/models/P11_Cage/P11_Cage.glb';
          break;
        }
        case 'P11_Hall': {
          hash.model = 'assets/models/P11_Hall/P11_Hall.glb';
          break;
        }
        case 'R9_Cage': {
          hash.model = 'assets/models/R9_Cage/R9_Cage.glb';
          break;
        }
        case 'R9_Hall': {
          hash.model = 'assets/models/R9_Hall/R9_Hall.glb';
          break;
        }
        case 'B750_Cage': {
          hash.model = 'assets/models/B750_Cage/B750_Cage.glb';
          break;
        }
        case 'B750_Hall': {
          hash.model = 'assets/models/B750_Hall/B750_Hall.glb';
          break;
        }
        case 'EP26_Cage': {
          hash.model = 'assets/models/EP26_Cage/EP26_Cage.glb';
          break;
        }
        case 'EP26_Hall': {
          hash.model = 'assets/models/EP26_Hall/EP26_Hall.glb';
          break;
        }
        case 'P24_Cage': {
          hash.model = 'assets/models/P24_Cage/P24_Cage.glb';
          break;
        }
        case 'P24_Hall': {
          hash.model = 'assets/models/P24_Hall/P24_Hall.glb';
          break;
        }
      }
    }
    this.options = {
      kiosk: Boolean(hash.kiosk),
      model: hash.model || '',
      preset: hash.preset || '',
      cameraPosition: hash.cameraPosition
        ? hash.cameraPosition.split(',').map(Number)
        : null,
    };
    location.hash = '';

    this.el = el;
    this.viewer = null;
    this.viewerEl = null;
    this.spinnerEl = el.querySelector('.loader');
    this.coverEl = el.querySelector('.cover');
    this.dropEl = el.querySelector('.top');
    // this.inputEl = el.querySelector('#file-input');
    this.UIEl = el.querySelector('#UI');
    this.controlEl = el.querySelector('#view_control');

    // this.createDropzone();
    this.hideSpinner();

    const options = this.options;

    if (options.kiosk) {
      const headerEl = document.querySelector('header');
      headerEl.style.display = 'none';
    }

    if (options.model) {
      this.view(options.model, '', new Map());
    }
  }

  /**
   * Sets up the drag-and-drop controller.
   */
  createDropzone() {
    const dropCtrl = new SimpleDropzone(this.dropEl, this.inputEl);
    dropCtrl.on('drop', ({ files }) => this.load(files));
    dropCtrl.on('dropstart', () => this.showSpinner());
    dropCtrl.on('droperror', () => this.hideSpinner());
  }

  /**
   * Sets up the view manager.
   * @return {Viewer}
   */
  createViewer() {
    this.viewerEl = document.createElement('div');
    this.viewerEl.classList.add('viewer');
    this.dropEl.innerHTML = '';
    this.dropEl.appendChild(this.viewerEl);
    this.viewer = new Viewer(this.viewerEl, this.options);
    return this.viewer;
  }

  /**
   * Loads a fileset provided by user action.
   * @param  {Map<string, File>} fileMap
   */
  load(fileMap) {
    let rootFile;
    let rootPath;
    Array.from(fileMap).forEach(([path, file]) => {
      if (file.name.match(/\.(gltf|glb)$/)) {
        rootFile = file;
        rootPath = path.replace(file.name, '');
      }
    });

    if (!rootFile) {
      this.onError('No .gltf or .glb asset found.');
    }

    this.view(rootFile, rootPath, fileMap);
  }

  /**
   * Passes a model to the viewer, given file and resources.
   * @param  {File|string} rootFile
   * @param  {string} rootPath
   * @param  {Map<string, File>} fileMap
   */
  view(rootFile, rootPath, fileMap) {
    this.showSpinner();
    this.showUI();

    if (this.viewer) this.viewer.clear();

    const viewer = this.viewer || this.createViewer();

    const fileURL =
      typeof rootFile === 'string' ? rootFile : URL.createObjectURL(rootFile);

    const cleanup = () => {
      this.hideSpinner();
      if (typeof rootFile === 'object') URL.revokeObjectURL(fileURL);
    };

    viewer
      .load(fileURL, rootPath, fileMap)
      .catch((e) => this.onError(e))
      .then((gltf) => {
        cleanup();
      });
  }

  /**
   * @param  {Error} error
   */
  onError(error) {
    let message = (error || {}).message || error.toString();
    if (message.match(/ProgressEvent/)) {
      message =
        'Unable to retrieve this file. Check JS console and browser network tab.';
    } else if (message.match(/Unexpected token/)) {
      message = `Unable to parse file content. Verify that this file is valid. Error: "${message}"`;
    } else if (error && error.target && error.target instanceof Image) {
      message = 'Missing texture: ' + error.target.src.split('/').pop();
    }
    window.alert(message);
    console.error(error);
  }

  loadModel(modelPath) {
    this.view(modelPath, '', new Map());
  }

  showSpinner() {
    this.spinnerEl.style.display = '';
    this.coverEl.classList.add('show');
  }

  hideSpinner() {
    this.spinnerEl.style.display = 'none';
    this.coverEl.classList.remove('show');
  }

  showUI() {
    this.UIEl.style.display = 'flex';
    this.controlEl.style.display = 'flex';
    this.dropEl.classList.remove('top');
    this.dropEl.classList.add('viewwrap');
  }

  activateSelector(id, subtype, element) {
    if (!document.getElementById(id)) return;

    if (subtype == undefined) {
      Array.from(document.getElementsByClassName('ev_place')).forEach((el) => {
        el.style.display = 'none';
      });
      Array.from(document.getElementsByClassName('ev_subtype')).forEach(
        (el) => {
          el.style.filter = '';
        }
      );
      Array.from(
        document.querySelectorAll('.ev_type > .type_cap > img')
      ).forEach((img, i) => {
        switch (i) {
          case 0: {
            img.src = 'assets/images/02_top/top_tx3.png';
            break;
          }
        }
      });
    } else {
      Array.from(document.getElementsByClassName('ev_subtype')).forEach(
        (el) => {
          el.style.filter = '';
        }
      );
      element.style.filter = 'brightness(100%)';

      element.parentNode.nextElementSibling.children[0].setAttribute(
        'onclick',
        `loadModel('assets/models/${subtype}_Cage/${subtype}_Cage.glb')`
      );
      element.parentNode.nextElementSibling.children[1].setAttribute(
        'onclick',
        `loadModel('assets/models/${subtype}_Hall/${subtype}_Hall.glb')`
      );

      const capImg =
        element.parentNode.parentNode.querySelector('.type_cap > img');
      switch (subtype) {
        case 'P11': {
          capImg.src = 'assets/images/02_top/top_tx3.png';
          break;
        }
        case 'P24': {
          capImg.src = 'assets/images/02_top/top_tx3_1.png';
          break;
        }
      }
    }

    document.getElementById(id).style.display = 'flex';
  }

  sectionClick(e, id) {
    const el = document.getElementById(id);
    if (!el) return;

    el.classList.toggle('hidden');
    switch (id) {
      case 'type_ui': {
        if (el.classList.contains('hidden'))
          e.target.src = 'assets/images/00_navigation/nav_typ_off.png';
        else e.target.src = 'assets/images/00_navigation/nav_typ_on.png';
        break;
      }
      case 'sui_ui': {
        if (el.classList.contains('hidden'))
          e.target.src = 'assets/images/00_navigation/nav_sui_off.png';
        else e.target.src = 'assets/images/00_navigation/nav_sui_on.png';
        break;
      }
      case 'custom_ui': {
        if (el.classList.contains('hidden'))
          e.target.src = 'assets/images/00_navigation/nav_cus_off.png';
        else e.target.src = 'assets/images/00_navigation/nav_cus_on.png';
        break;
      }
      case 'ui_content': {
        if (el.classList.contains('hidden')) {
          // close
          document.getElementById('UI').style.width = 'auto';
          document.getElementById('UI').style.minWidth = '0';
          document.querySelector('#ui_control>img').src =
            'assets/images/00_navigation/nav_2.png';
          this.viewer.resize();
        } else {
          // open
          document.getElementById('UI').style.width = '';
          document.getElementById('UI').style.minWidth = '';
          document.querySelector('#ui_control>img').src =
            'assets/images/00_navigation/nav_1.png';
          this.viewer.resize();
        }
        break;
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const app = new App(document.body, location);
});
